




































import ConfirmActionDialog from "@/components/utility/ConfirmActionDialog.vue";
import { RequestStatusEnum } from "@/lib/enum/request-status-enum";
import { IDialog } from "@/lib/interfaces/dialog.interface";
import { simpleDoubleDigitDate } from "@/lib/utility/date-helper";
import { handleError } from "@/lib/utility/handleError";
import { $t } from "@/lib/utility/t";
import PermissionMixin from "@/mixins/PermissionMixin.vue";
import { ClearanceApproval, IClearanceApproval } from "@/models/clearance-approval.entity";
import { IClearance, Clearance } from "@/models/clearance.entity";
import { InvoiceParty } from "@/models/invoice-party.entity";
import { IReference } from "@/models/reference.entity";
import { IRepairEstimate } from "@/models/repair-estimate.entity";
import { IReport } from "@/models/report.entity";
import { ISharedContent } from "@/models/shared-content.entity";
import { UserModule } from "@/store/modules/me-user.store";
import { mixins } from "vue-class-component";
import { Component, Prop, Ref, Watch } from "vue-property-decorator";
import ClearanceApprovalCreateStepper, { ClearanceApprovalSteps } from "./ClearanceApprovalCreateStepper.vue";

@Component({
  components: {
    ClearanceApprovalCreateStepper,
    ConfirmActionDialog
  },
  filters: {
    simpleDoubleDigitDate
  }
})
export default class ClearanceApprovalCreateRequestDialog extends mixins(PermissionMixin) implements IDialog {
  readonly ClearanceApprovalSteps = ClearanceApprovalSteps;

  @Prop({ required: false })
  clearance!: IClearance;

  @Ref("requestClearanceApprovalStepper")
  requestClearanceApprovalStepper!: ClearanceApprovalCreateStepper;

  @Prop()
  partnerId!: string;

  @Prop()
  hideButton!: boolean;

  @Prop({ default: () => [] })
  refs!: IReference[];

  @Prop({ default: false })
  hideRefsSelect!: boolean;

  @Prop()
  sellerCompanyId?: string;

  @Prop()
  report!: IReport;

  @Prop()
  repairEstimate!: IRepairEstimate;

  isDialogActive = false;

  isLoading = false;

  requestsLoading = false;

  step = ClearanceApprovalSteps.SELLER;

  isCreatingAnother = false;

  value: IClearanceApproval | null = null;

  get steps() {
    return new Map([
      [
        ClearanceApprovalSteps.SELLER,
        {
          title: this.$t("clearanceApproval.request"),
          subtitle: this.$t("clearanceApproval.seller.subtitle"),
          backText: $t("close"),
          nextText: $t("next"),
          loading: false,
          disabled: !this.value?.seller?.ref?.refId,
          back: () => {
            this.close();
          },
          next: async () => {
            if (this.isCreatingAnother) {
              // skip line items and expiration steps if creating another request. Just select seller and send mail
              this.requestsLoading = true;
              await this.requestClearanceApprovalStepper?.createRequests();
              this.requestsLoading = false;
              this.step = ClearanceApprovalSteps.SEND_MAIL;
              return;
            }
            this.step = ClearanceApprovalSteps.LINE_ITEMS;
          }
        }
      ],
      [
        ClearanceApprovalSteps.LINE_ITEMS,
        {
          title: this.$t("clearanceApproval.lineItems.title"),
          subtitle: this.$t("clearanceApproval.lineItems.subtitle"),
          backText: $t("back"),
          nextText: $t("next"),
          loading: this.requestsLoading,
          disabled: !this.value?.title || !this.value?.currency || !this.value?.lineItems?.length,
          back: () => {
            this.step = ClearanceApprovalSteps.SELLER;
          },
          next: () => {
            this.step = ClearanceApprovalSteps.LINE_ITEMS;
          }
        }
      ],

      [
        ClearanceApprovalSteps.LINE_ITEMS,
        {
          title: this.$t("clearanceApproval.clearanceSumTitle"),
          subtitle: this.$t("clearanceApproval.clearanceSumTitleHint"),
          backText: $t("back"),
          nextText: $t("next"),
          loading: this.requestsLoading,
          disabled: !this.value?.title || !this.value?.currency || !this.value?.lineItems?.length,
          back: () => {
            this.step = ClearanceApprovalSteps.SELLER;
          },
          next: async () => {
            this.step = ClearanceApprovalSteps.EXPIRATION;
          }
        }
      ],
      [
        ClearanceApprovalSteps.EXPIRATION,
        {
          title: this.$t("clearanceApproval.untilWhen"),
          subtitle: this.$t("clearanceApproval.untilWhenHint"),
          backText: $t("back"),
          nextText: $t("create"),
          loading: this.requestsLoading,
          back: () => {
            this.step = ClearanceApprovalSteps.LINE_ITEMS;
          },
          next: async () => {
            this.requestsLoading = true;
            await this.requestClearanceApprovalStepper?.createRequests();
            this.requestsLoading = false;
            this.step = ClearanceApprovalSteps.SEND_MAIL;
          }
        }
      ],
      [
        ClearanceApprovalSteps.SEND_MAIL,
        {
          title: this.$t("clearanceApproval.sendMail"),
          backText: $t("close"),
          nextText: $t("clearanceApproval.sendMail"),
          loading: false,
          back: () => {
            this.close();
          },
          next: async () => {
            try {
              await this.requestClearanceApprovalStepper?.templateCard.send();
              await this.value?.updatePartial({
                status: RequestStatusEnum.REQUESTED
              });
              this.step = ClearanceApprovalSteps.SUCCESS;
            } catch (e) {
              handleError(e);
            }
          }
        }
      ],
      [
        ClearanceApprovalSteps.SUCCESS,
        {
          title: this.$t("clearanceApproval.success"),
          backText: $t("close"),
          nextText: $t("clearance.createAnother"),
          loading: false,
          back: () => this.close(),
          next: () => this.createAnother()
        }
      ]
    ]);
  }

  get currentStep() {
    return this.steps.get(this.step);
  }

  get user() {
    return UserModule.user;
  }

  open() {
    this.value = new ClearanceApproval({}).mapFromClearance(
      this.clearance ?? new Clearance({ partnerId: this.partnerId })
    );

    this.isCreatingAnother = false;
    this.reset();

    if (this.requestClearanceApprovalStepper) {
      this.value.seller = new InvoiceParty();
      this.requestClearanceApprovalStepper.setSellerFromCompanyId();
    }

    this.step = ClearanceApprovalSteps.SELLER;
    this.isDialogActive = true;

    this.onReportIdChange();
  }

  @Watch("value.reportId")
  async onReportIdChange() {
    await this.value?.fetchReportAndVehicle().catch(this.$log.error);
  }

  async openShareExisting(clearanceApproval: IClearanceApproval, sharedContent?: ISharedContent) {
    this.open();

    this.$nextTick(() => {
      if (this.requestClearanceApprovalStepper) {
        this.value = clearanceApproval;
        this.requestClearanceApprovalStepper.stepLocal = ClearanceApprovalSteps.EXPIRATION;
        if (sharedContent) {
          this.requestClearanceApprovalStepper.sharedContent = sharedContent;
          this.requestClearanceApprovalStepper.stepLocal = ClearanceApprovalSteps.SEND_MAIL;
        }
      }
    });
  }

  close() {
    this.reset();
    this.isDialogActive = false;
    this.requestClearanceApprovalStepper?.created?.splice(0);
  }

  createAnother() {
    this.reset();
    this.isCreatingAnother = true;
  }

  reset() {
    this.value = new ClearanceApproval({}).mapFromClearance(
      this.clearance ?? new Clearance({ partnerId: this.partnerId })
    );
    this.step = ClearanceApprovalSteps.SELLER;
    this.isLoading = false;
  }
}
